import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

import { BASE_URL, getImageUrl } from '../../services/api';
import {
  Mail,
  MapPin,
  Phone,
  Building2,
  Users,
  BookOpen,
  Calendar,
  Images as ImageIcon,
  FileText,
  ChevronRight,
  Facebook,
  Linkedin
} from 'lucide-react';
import defaultLogo from '../../assets/logo.png';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [generalInfo, setGeneralInfo] = React.useState<any>(null);

  React.useEffect(() => {
    const fetchGeneralInfo = async () => {
      try {
        const { adminService } = await import('../../services/admin.service');
        const info = await adminService.getGeneralInfo();
        setGeneralInfo(info);
      } catch (error) {
        console.error('Failed to load general info for footer', error);
      }
    };
    fetchGeneralInfo();
  }, []);

  const footerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  const footerLinks = {
    navigation: [
      { to: '/', label: 'Accueil' },
      { to: '/Presentation', label: 'Présentation' },
      { to: '/Actualite', label: 'Actualités' },
      { to: '/Contact', label: 'Contact' },
    ],
    resources: [
      { to: '/Evenement', label: 'Événements', icon: Calendar },
      { to: '/Publication', label: 'Publications', icon: FileText },
      { to: '/Galerie', label: 'Galerie', icon: ImageIcon },
      { to: '/adhesion', label: 'Adhésions', icon: Users },
    ],
    about: [
      { to: '/Presentation', label: 'À propos' },
      { to: '/Contact', label: 'Nous contacter' },
      { to: '/adhesion', label: 'Devenir membre' },
      { to: '/Actualite', label: 'Actualités' },
    ]
  };

  return (
    <motion.footer
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={footerVariants}
      className="bg-gray-900 text-white"
    >
      {/* Horizontal Navbar */}


      {/* Main Footer Content */}
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 lg:gap-8">
          {/* Company Info Column */}
          <motion.div variants={itemVariants} className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              <img src={defaultLogo} alt="SAPI" className="h-16 w-auto object-contain" />
            </Link>
            <p className="text-gray-300 text-sm leading-relaxed mb-6 max-w-md">
              La Société Algérienne de l'Industrie Pharmaceutique est une association scientifique à but non lucratif créée en avril 2024. Elle rassemble les universitaires, chercheurs, pharmaciens, ingénieurs et industriels œuvrant dans le domaine de la pharmacie industrielle.
            </p>
            <div className="flex flex-col gap-3 mb-6">
              <div className="flex items-start gap-3 text-gray-300 text-sm">
                <MapPin className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <span>{generalInfo?.address || 'Alger, Algérie'}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-300 text-sm">
                <Phone className="w-5 h-5 text-secondary flex-shrink-0" />
                <a href={`tel:${generalInfo?.phone || '+213000000000'}`} className="hover:text-secondary transition-colors">
                  {generalInfo?.phone || '+213 000 000 000'}
                </a>
              </div>
              <div className="flex items-center gap-3 text-gray-300 text-sm">
                <Mail className="w-5 h-5 text-secondary flex-shrink-0" />
                <a href={`mailto:${generalInfo?.emailcontact || 'contact@sapi-dz.org'}`} className="hover:text-secondary transition-colors">
                  {generalInfo?.emailcontact || 'contact@sapi-dz.org'}
                </a>
              </div>
            </div>
            {/* Social Media Icons */}
            <div className="flex items-center gap-4">
              <a
                href="https://www.facebook.com/people/Soci%C3%A9t%C3%A9-Alg%C3%A9rienne-de-Pharmacie-Industrielle-SAPI/61584198141460/?rdid=pU1iaUXetsNM4KgU&share_url=https%3A%2F%2Fwww.facebook.com%2Fshare%2F18M6UnBpub%2F"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-800 hover:bg-secondary/20 flex items-center justify-center text-gray-300 hover:text-secondary transition-all duration-300 group"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5 group-hover:scale-110 transition-transform" />
              </a>
              <a
                href="https://www.linkedin.com/company/aspidz/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-800 hover:bg-secondary/20 flex items-center justify-center text-gray-300 hover:text-secondary transition-all duration-300 group"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5 group-hover:scale-110 transition-transform" />
              </a>
            </div>
          </motion.div>

          {/* Navigation Column */}
          <motion.div variants={itemVariants}>
            <h3 className="text-white font-bold text-lg mb-6 flex items-center gap-2">
              Navigation
              <ChevronRight className="w-4 h-4 text-secondary" />
            </h3>
            <ul className="space-y-3">
              {footerLinks.navigation.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-gray-300 hover:text-secondary text-sm transition-colors duration-200 flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 bg-secondary rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Resources Column */}
          <motion.div variants={itemVariants}>
            <h3 className="text-white font-bold text-lg mb-6 flex items-center gap-2">
              Ressources
              <ChevronRight className="w-4 h-4 text-secondary" />
            </h3>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => {
                const Icon = link.icon || ChevronRight;
                return (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="text-gray-300 hover:text-secondary text-sm transition-colors duration-200 flex items-center gap-2 group"
                    >
                      <Icon className="w-4 h-4 text-secondary/70 group-hover:text-secondary transition-colors" />
                      {link.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </motion.div>

          {/* About Column */}
          <motion.div variants={itemVariants}>
            <h3 className="text-white font-bold text-lg mb-6 flex items-center gap-2">
              À propos
              <ChevronRight className="w-4 h-4 text-secondary" />
            </h3>
            <ul className="space-y-3">
              {footerLinks.about.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-gray-300 hover:text-secondary text-sm transition-colors duration-200 flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 bg-secondary rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>

      {/* Bottom Bar - All Rights Reserved */}
      <motion.div
        variants={itemVariants}
        className="bg-gray-950"
      >
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8 text-center md:text-left">
              <p className="text-gray-400 text-sm">
                © {currentYear} <span className="text-secondary font-semibold">SAPI</span> - Société Algérienne de l'Industrie Pharmaceutique
              </p>
              <span className="hidden md:block text-gray-600">•</span>
              <p className="text-gray-500 text-xs">
                Tous droits réservés
              </p>
            </div>

          </div>
        </div>
      </motion.div>
    </motion.footer>
  );
};

export default Footer;
